import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEscalationLevel } from '../../customer/context/EscalationContext';
import { useEventManagementService } from '../services/eventManagementService';
import { isBreakingChange, plainWriteRefusal } from '../utils/datasetFormUtils';
import type {
  CreateCustomerEventDatasetTemplateRequest,
  EventDataset,
  EventDatasetMisfit,
  EventDatasetPreview,
  EventDatasetRecord,
  EventDatasetRetentionCeiling,
  EventDatasetRows,
  EventDatasetStatus,
  EventDatasetTemplate,
  QueryEventDatasetRequest,
} from '../../../types/backend';

export const eventDatasetKeys = {
  list: (customerId: string) => ['event-management', 'datasets', customerId] as const,
  // Kept apart from the dataset keys, so a dataset write does not reread the templates.
  templates: (customerId: string) => ['event-management', 'dataset-templates', customerId] as const,
  retentionCeiling: (customerId: string) => ['event-management', 'datasets', customerId, 'retention-ceiling'] as const,
  status: (customerId: string, name: string) => ['event-management', 'datasets', customerId, name, 'status'] as const,
  misfits: (customerId: string, name: string, column: string, kind: EventDatasetMisfit['kind']) =>
    ['event-management', 'datasets', customerId, name, 'misfits', column, kind] as const,
  rows: (request: QueryEventDatasetRequest) =>
    ['event-management', 'datasets', request.customer_id, request.name, 'rows', request] as const,
  count: (read: QueryEventDatasetRequest) =>
    ['event-management', 'datasets', read.customer_id, read.name, 'count', read] as const,
};

// The backend caps a misfit read at 100 events, newest first.
export const MISFIT_LIMIT = 100;

interface DatasetsResult {
  datasets: EventDatasetRecord[];
  loading: boolean;
  error: string | null;
}

interface DatasetStatusResult {
  status: EventDatasetStatus | undefined;
  loading: boolean;
  error: string | null;
}

interface DatasetMisfitsResult {
  misfits: EventDatasetMisfit[];
  loading: boolean;
  error: string | null;
}

// A dataset in motion is read again every 15 s, a settled one every minute; neither while the tab is hidden.
const MOVING_REFETCH_MS = 15_000;
const SETTLED_REFETCH_MS = 60_000;

const messageOf = (error: Error | null): string | null => (error ? error.message || 'Read failed' : null);
// A write the Cedar wall refused reads as the level it needs, not as the action it names.
const refusalOf = (error: Error | null): string | null => {
  const message = messageOf(error);
  return message ? plainWriteRefusal(message) : null;
};

export function useEventDatasets(customerId: string): DatasetsResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.list(customerId),
    queryFn: () => service.listEventDatasets(customerId),
    enabled: !!customerId,
    // A deleting dataset leaves the list once the reconciler has removed it.
    refetchInterval: (q) =>
      q.state.data?.some((dataset) => dataset.status === 'deleting') ? MOVING_REFETCH_MS : false,
  });
  return { datasets: query.data ?? [], loading: query.isPending, error: messageOf(query.error) };
}

export function useEventDatasetStatus(customerId: string, name: string): DatasetStatusResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.status(customerId, name),
    queryFn: () => service.getEventDatasetStatus(customerId, name),
    enabled: !!customerId && !!name,
    refetchInterval: (q) => {
      const state = q.state.data?.state;
      return state === 'backfilling' || state === 'deleting' ? MOVING_REFETCH_MS : SETTLED_REFETCH_MS;
    },
  });
  // A failed read keeps no earlier answer: a stale "current" must not outlive the error that replaced it.
  const status = query.error ? undefined : query.data;
  return { status, loading: query.isPending, error: messageOf(query.error) };
}

interface RetentionCeilingResult {
  ceiling: EventDatasetRetentionCeiling | undefined;
  error: string | null;
}

/** The raw archive's retention, which caps a dataset's; read only while a new dataset is being defined. */
export function useEventDatasetRetentionCeiling(customerId: string, enabled: boolean): RetentionCeilingResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.retentionCeiling(customerId),
    queryFn: () => service.getEventDatasetRetentionCeiling(customerId),
    enabled: enabled && !!customerId,
  });
  return { ceiling: query.error ? undefined : query.data, error: messageOf(query.error) };
}

/**
 * Whether the dataset writes (create, update, delete and rebuild) show: they mirror the Cedar wall, which reads
 * the effective level. The level is the one the access badge reports for the selected customer, so an escalation
 * granted or lapsed there reaches the dataset screens with the badge, the one place to escalate.
 */
export function useDatasetWriteAccess(): boolean {
  return useEscalationLevel().effectiveLevel === 'admin';
}

export interface DatasetRefusal {
  message: string;
  /** The backend refused a non-additive change to a saved dataset; a new dataset is the way out. */
  breaking: boolean;
}

/** What the caller does once a write is done or refused; the refusal also stays readable on the hook. */
export interface MutationOutcome<T> {
  onDone: (result: T) => void;
  onFailed: (message: string) => void;
}

interface SaveDatasetResult {
  save: (dataset: EventDataset, isEdit: boolean, outcome: MutationOutcome<EventDatasetRecord>) => void;
  saving: boolean;
  refusal: DatasetRefusal | null;
}

export function useSaveEventDataset(customerId: string): SaveDatasetResult {
  const service = useEventManagementService();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ dataset, isEdit }: { dataset: EventDataset; isEdit: boolean }) =>
      isEdit ? service.updateEventDataset(dataset) : service.createEventDataset(dataset),
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.list(customerId) });
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.status(customerId, record.name) });
    },
  });
  const message = refusalOf(mutation.error);
  return {
    save: (dataset, isEdit, { onDone, onFailed }) => mutation.mutate(
      { dataset, isEdit },
      { onSuccess: (record) => onDone(record), onError: (error) => onFailed(plainWriteRefusal(error.message)) },
    ),
    saving: mutation.isPending,
    refusal: message ? { message, breaking: isBreakingChange(message) } : null,
  };
}

interface PreviewDatasetResult {
  preview: (draft: EventDataset, sample: Record<string, unknown> | undefined) => void;
  result: EventDatasetPreview | undefined;
  previewing: boolean;
  error: string | null;
}

/** A preview is read live and shown once; it is held in no query cache and never saved. */
export function usePreviewEventDataset(customerId: string): PreviewDatasetResult {
  const service = useEventManagementService();
  const mutation = useMutation({
    mutationFn: ({ draft, sample }: { draft: EventDataset; sample: Record<string, unknown> | undefined }) =>
      service.previewEventDataset(customerId, draft, sample),
    // The rows and the pasted sample leave the mutation cache as soon as the preview unmounts.
    gcTime: 0,
  });
  return {
    preview: (draft, sample) => mutation.mutate({ draft, sample }),
    result: mutation.error ? undefined : mutation.data,
    previewing: mutation.isPending,
    error: messageOf(mutation.error),
  };
}

interface DeleteDatasetResult {
  remove: (name: string, outcome: MutationOutcome<unknown>) => void;
  deleting: boolean;
  error: string | null;
  /** Drops the last refusal, so a dialog opened again starts without it. */
  reset: () => void;
}

export function useDeleteEventDataset(customerId: string): DeleteDatasetResult {
  const service = useEventManagementService();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (name: string) => service.deleteEventDataset(customerId, name),
    onSuccess: (_deleted, name) => {
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.list(customerId) });
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.status(customerId, name) });
    },
  });
  return {
    remove: (name, { onDone, onFailed }) =>
      mutation.mutate(name, {
        onSuccess: (deleted) => onDone(deleted),
        onError: (error) => onFailed(plainWriteRefusal(error.message)),
      }),
    deleting: mutation.isPending,
    error: refusalOf(mutation.error),
    reset: mutation.reset,
  };
}

interface RebuildDatasetResult {
  rebuild: (name: string, outcome: MutationOutcome<unknown>) => void;
  rebuilding: boolean;
  error: string | null;
  /** Drops the last refusal, so a dialog opened again starts without it. */
  reset: () => void;
}

export function useRebuildEventDataset(customerId: string): RebuildDatasetResult {
  const service = useEventManagementService();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (name: string) => service.rebuildEventDataset(customerId, name),
    onSuccess: (_record, name) => {
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.list(customerId) });
      queryClient.invalidateQueries({ queryKey: eventDatasetKeys.status(customerId, name) });
    },
  });
  return {
    rebuild: (name, { onDone, onFailed }) =>
      mutation.mutate(name, {
        onSuccess: (rebuilt) => onDone(rebuilt),
        onError: (error) => onFailed(plainWriteRefusal(error.message)),
      }),
    rebuilding: mutation.isPending,
    error: refusalOf(mutation.error),
    reset: mutation.reset,
  };
}

// A template read or write that failed reads as one plain sentence; the backend's own text is never shown.
export const TEMPLATES_READ_FAILED = 'The dataset templates could not be read. Try again in a moment.';
export const TEMPLATE_SAVE_FAILED = 'The template could not be saved. Check that its name is not already used.';
export const TEMPLATE_DELETE_FAILED = 'The template could not be deleted. Try again in a moment.';

const plainTemplateFailure = (error: Error, fallback: string): string => {
  const refusal = plainWriteRefusal(error.message);
  return refusal === error.message ? fallback : refusal;
};

interface DatasetTemplatesResult {
  templates: EventDatasetTemplate[];
  loading: boolean;
  error: string | null;
}

/** The templates a new dataset can start from: the global ones and this customer's own. */
export function useEventDatasetTemplates(customerId: string, enabled = true): DatasetTemplatesResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.templates(customerId),
    queryFn: () => service.listEventDatasetTemplates(customerId),
    enabled: enabled && !!customerId,
  });
  return {
    templates: query.data ?? [],
    loading: query.isPending && enabled,
    error: query.error ? TEMPLATES_READ_FAILED : null,
  };
}

/** A customer template to create: the request without the customer, which the hook supplies. */
export type TemplateDraft = Omit<CreateCustomerEventDatasetTemplateRequest, 'customer_id'>;

interface SaveDatasetTemplateResult {
  saveTemplate: (draft: TemplateDraft, outcome: MutationOutcome<EventDatasetTemplate>) => void;
  saving: boolean;
  error: string | null;
  reset: () => void;
}

/** Saves a definition as a template this customer owns; global templates are not authored from this screen. */
export function useSaveEventDatasetTemplate(customerId: string): SaveDatasetTemplateResult {
  const service = useEventManagementService();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (draft: TemplateDraft) => service.createCustomerEventDatasetTemplate(customerId, draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventDatasetKeys.templates(customerId) }),
  });
  return {
    saveTemplate: (draft, { onDone, onFailed }) => mutation.mutate(draft, {
      onSuccess: (template) => onDone(template),
      onError: (error) => onFailed(plainTemplateFailure(error, TEMPLATE_SAVE_FAILED)),
    }),
    saving: mutation.isPending,
    error: mutation.error ? plainTemplateFailure(mutation.error, TEMPLATE_SAVE_FAILED) : null,
    reset: mutation.reset,
  };
}

interface DeleteDatasetTemplateResult {
  removeTemplate: (templateId: string, outcome: MutationOutcome<unknown>) => void;
  deleting: boolean;
  error: string | null;
  reset: () => void;
}

export function useDeleteEventDatasetTemplate(customerId: string): DeleteDatasetTemplateResult {
  const service = useEventManagementService();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (templateId: string) => service.deleteEventDatasetTemplate(customerId, templateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventDatasetKeys.templates(customerId) }),
  });
  return {
    removeTemplate: (templateId, { onDone, onFailed }) => mutation.mutate(templateId, {
      onSuccess: (deleted) => onDone(deleted),
      onError: (error) => onFailed(plainTemplateFailure(error, TEMPLATE_DELETE_FAILED)),
    }),
    deleting: mutation.isPending,
    error: mutation.error ? plainTemplateFailure(mutation.error, TEMPLATE_DELETE_FAILED) : null,
    reset: mutation.reset,
  };
}

/** One kind of a column's events, misfits or untranslated notes, so neither takes the other's place on the page. */
export function useEventDatasetMisfits(
  customerId: string,
  name: string,
  column: string,
  kind: EventDatasetMisfit['kind'],
): DatasetMisfitsResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.misfits(customerId, name, column, kind),
    queryFn: () => service.listEventDatasetMisfits(customerId, name, column, kind, MISFIT_LIMIT),
    enabled: !!customerId && !!name && !!column,
    // Misfits are read live from the event archive on every open; nothing is kept between opens.
    staleTime: 0,
    gcTime: 0,
  });
  return { misfits: query.data ?? [], loading: query.isPending, error: messageOf(query.error) };
}

interface DatasetRowsResult {
  rows: EventDatasetRows | undefined;
  loading: boolean;
  /** The rows shown are the previous read's, kept while this one is read. */
  stale: boolean;
  error: string | null;
  /** Reads the same page again. */
  refetch: () => void;
}

/**
 * One page of a dataset's rows (EM-UC-37). Each read is an Athena query in the customer's account, so a page is
 * read when asked for and again only on Refresh or on going back to it, never on a focus or an interval, and its
 * rows leave the cache as soon as the page is left.
 */
export function useEventDatasetRows(request: QueryEventDatasetRequest): DatasetRowsResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.rows(request),
    queryFn: () => service.queryEventDataset(request),
    enabled: !!request.customer_id && !!request.name,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    // A changed page or filter keeps the shown rows until the next read returns.
    placeholderData: keepPreviousData,
  });
  return {
    rows: query.error ? undefined : query.data,
    loading: query.isFetching,
    stale: query.isPlaceholderData,
    error: messageOf(query.error),
    refetch: () => void query.refetch(),
  };
}

interface CountDatasetRowsResult {
  matched: number | undefined;
  counting: boolean;
  error: string | null;
  /** Counts the same read again, such as after a failed count. */
  refetch: () => void;
}

/**
 * How many rows a read matches: one count_only read, run only once the operator asks for it (`requested`). A
 * changed read is a new key, so the count of the old read is not shown for it.
 */
export function useEventDatasetRowCount(read: QueryEventDatasetRequest, requested: boolean): CountDatasetRowsResult {
  const service = useEventManagementService();
  const query = useQuery({
    queryKey: eventDatasetKeys.count(read),
    queryFn: () => service.queryEventDataset({ ...read, count_only: true }),
    enabled: requested && !!read.customer_id && !!read.name,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    matched: query.error ? undefined : query.data?.rows_matched,
    counting: query.isFetching,
    error: messageOf(query.error),
    refetch: () => void query.refetch(),
  };
}
